import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Calendar, MapPin, ExternalLink } from "lucide-react";
import { format } from "date-fns";
import { proxyGoogleImage } from "../lib/googleImageProxy";

interface Post {
  post_id: string;
  google_post_id: string;
  status: string;
  published_at: string;
  metadata: any;
  post_content: {
    id: string;
    summary: string;
    media_urls?: string[];
    type?: string;
    call_to_action?: any;
    event_info?: any;
    offer_info?: any;
  };
}

interface LandingPostsSectionProps {
  locationId: string;
  theme: any;
  content?: any;
  isStandalonePage?: boolean;
  ssrPostsData?: any[] | null;
}

const LandingPostsSection = ({ locationId, theme, content, isStandalonePage = false, ssrPostsData }: LandingPostsSectionProps) => {
  const { subdomain } = useParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>(ssrPostsData || []);
  const [loading, setLoading] = useState(!ssrPostsData);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Get computed theme colors from CSS variables for portal-rendered modals
  const getThemeColors = () => {
    const defaults = {
      bg: '#ffffff',
      text: '#000000',
      textSecondary: '#666666',
      bgSecondary: '#f5f5f5',
      borderColor: '#e0e0e0',
    };

    if (typeof window === 'undefined') return defaults;
    const root = document.querySelector('.subdomain-landing-root');
    if (!root) return defaults;

    const styles = window.getComputedStyle(root);
    const bg = styles.getPropertyValue('--bg').trim();
    const text = styles.getPropertyValue('--text').trim();
    const textSecondary = styles.getPropertyValue('--text-secondary').trim();
    const bgSecondary = styles.getPropertyValue('--bg-secondary').trim();
    const borderColor = styles.getPropertyValue('--border-color').trim();

    return {
      bg: bg || defaults.bg,
      text: text || defaults.text,
      textSecondary: textSecondary || defaults.textSecondary,
      bgSecondary: bgSecondary || defaults.bgSecondary,
      borderColor: borderColor || defaults.borderColor,
    };
  };

  // Check if we're on an actual subdomain (subdomain.localhost) vs testing route (/subdomain/:subdomain)
  const isOnSubdomain = () => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      // subdomain.localhost or subdomain.my-domain.com
      return parts.length >= 2 && parts[0] !== 'localhost' && parts[0] !== 'www';
    }
    return false;
  };

  const getBasePath = () => {
    return isOnSubdomain() ? '' : `/subdomain/${subdomain}`;
  };

  useEffect(() => {
    // If we have SSR data, don't fetch again
    if (ssrPostsData) {
      return;
    }

    fetchPosts();
  }, [locationId, isStandalonePage, ssrPostsData]);

  const fetchPosts = async () => {
    try {
      console.log("Fetching posts for location:", locationId);

      // First, let's try to fetch just the post locations without the join
      const limit = isStandalonePage ? 12 : 6;
      const { data: postLocations, error: locationsError } = await supabase
        .from("gmb_post_locations")
        .select("*")
        .eq("location_id", locationId)
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(limit);

      if (locationsError) {
        console.error("Error fetching post locations:", locationsError);
        return;
      }

      console.log("Post locations fetched:", postLocations);

      // If we have post locations, try to fetch the content separately
      if (postLocations && postLocations.length > 0) {
        const postIds = postLocations.map(p => p.post_id);
        console.log("Fetching content for post IDs:", postIds);

        const { data: postContent, error: contentError } = await supabase
          .from("gmb_post_content")
          .select("*")
          .in("id", postIds);

        if (contentError) {
          console.error("Error fetching post content:", contentError);
          // Try to proceed with just location data if content fails
          const postsWithMetadata = postLocations.map(loc => ({
            ...loc,
            post_content: {
              id: loc.post_id,
              summary: loc.metadata?.summary || loc.metadata?.text || "",
              media_urls: loc.metadata?.media_urls || loc.metadata?.mediaUrls || [],
              type: loc.metadata?.type || loc.metadata?.postType || "STANDARD",
              call_to_action: loc.metadata?.call_to_action || loc.metadata?.callToAction,
              event_info: loc.metadata?.event_info || loc.metadata?.eventInfo,
              offer_info: loc.metadata?.offer_info || loc.metadata?.offerInfo
            }
          }));

          // Filter out posts with no actual content
          const postsWithContent = postsWithMetadata.filter(post =>
            post.post_content?.summary && post.post_content.summary.trim().length > 0
          );

          console.log("Using metadata fallback:", postsWithMetadata);
          console.log("Posts with content:", postsWithContent);
          setPosts(postsWithContent);
          return;
        }

        console.log("Post content fetched:", postContent);

        // Merge the data
        const mergedPosts = postLocations.map(loc => {
          const content = postContent?.find(c => c.id === loc.post_id);
          return {
            ...loc,
            post_content: content || {
              id: loc.post_id,
              summary: loc.metadata?.summary || loc.metadata?.text || "",
              media_urls: loc.metadata?.media_urls || loc.metadata?.mediaUrls || [],
              type: loc.metadata?.type || loc.metadata?.postType || "STANDARD",
              call_to_action: loc.metadata?.call_to_action || loc.metadata?.callToAction,
              event_info: loc.metadata?.event_info || loc.metadata?.eventInfo,
              offer_info: loc.metadata?.offer_info || loc.metadata?.offerInfo
            }
          };
        });

        // Filter out posts with no actual content
        const postsWithContent = mergedPosts.filter(post =>
          post.post_content?.summary && post.post_content.summary.trim().length > 0
        );

        console.log("Merged posts:", mergedPosts);
        console.log("Posts with content:", postsWithContent);

        // Debug each post in detail
        mergedPosts.forEach((post, index) => {
          console.log(`=== POST ${index + 1} DEBUG ===`);
          console.log("Post ID:", post.post_id);
          console.log("Post metadata:", post.metadata);
          console.log("Post content object:", post.post_content);
          console.log("Summary from content:", post.post_content?.summary);
          console.log("Media URLs from content:", post.post_content?.media_urls);
          console.log("Summary from metadata:", post.metadata?.summary);
          console.log("Media URLs from metadata:", post.metadata?.media_urls);
          console.log("========================");
        });

        setPosts(postsWithContent);
      } else {
        console.log("No published posts found for location");
        setPosts([]);
      }
    } catch (err) {
      console.error("Error fetching posts:", err);
    } finally {
      setLoading(false);
    }
  };

  const openPostModal = (post: Post) => {
    setSelectedPost(post);
    setModalOpen(true);
  };

  const getImageUrl = (mediaUrls: string[] | undefined) => {
    // Handle media_urls which is a text array
    if (!mediaUrls || !Array.isArray(mediaUrls) || mediaUrls.length === 0) {
      return null;
    }

    // Return the first URL in the array
    const firstUrl = mediaUrls[0];

    // If it's a valid URL, return it
    if (typeof firstUrl === 'string' && firstUrl.length > 0) {
      return firstUrl;
    }

    return null;
  };

  if (loading) {
    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Latest Updates</h2>
            <p className="text-gray-600">Loading posts...</p>
          </div>
        </div>
      </section>
    );
  }

  if (!posts || posts.length === 0) {
    return null; // Don't show section if no posts
  }

  return (
    <>
      <section id="posts" className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: "var(--primary)" }}
            >
              {content?.heading || "Latest Updates"}
            </h2>
            <div className="w-24 h-1 mx-auto mb-4" style={{ backgroundColor: "var(--primary)" }} />
            <p className="text-xl text-gray-600">{content?.description || "Stay updated with our latest news and offers"}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((post) => {
              const imageUrl = getImageUrl(post.post_content?.media_urls);
              console.log(`Post ${post.post_id} - Media URLs:`, post.post_content?.media_urls, "Image URL:", imageUrl);

              return (
                <Card
                  key={post.post_id}
                  className="post-card cursor-pointer hover:shadow-lg transition-shadow duration-300 overflow-hidden"
                  onClick={() => openPostModal(post)}
                >
                  {imageUrl && (
                    <div className="h-48 overflow-hidden bg-gray-100">
                      <img
                        src={proxyGoogleImage(imageUrl, 600)}
                        alt="Post image"
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          // Fires only when the (already proxied) URL genuinely fails.
                          console.error("Image failed to load after proxy:", imageUrl);
                          const container = e.currentTarget.parentElement;
                          if (container) container.style.display = 'none';
                        }}
                        onLoad={() => console.log("Image loaded successfully for post:", post.post_id)}
                      />
                    </div>
                  )}
                  <CardContent className="px-0 py-4">
                    <p className="text-gray-700 mb-4 overflow-hidden" style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      lineHeight: '1.5em',
                      maxHeight: '6em'
                    }}>
                      {post.post_content?.summary}
                    </p>
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>{format(new Date(post.published_at), "MMM d, yyyy")}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {!isStandalonePage && (
            <div className="text-center mt-12">
              <Button
                onClick={() => navigate(`${getBasePath()}/posts`)}
                style={{ backgroundColor: "var(--primary)", color: "var(--primary-foreground)" }}
                className="hover:opacity-90"
              >
                Show More Updates
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Post Detail Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="post-modal max-w-3xl max-h-[90vh] overflow-y-auto !bg-transparent !border-none !p-0">
          {selectedPost && (() => {
            const colors = getThemeColors();
            return (
              <div
                style={{
                  backgroundColor: colors.bg,
                  color: colors.text,
                  borderRadius: '12px',
                  padding: '24px',
                  border: `1px solid ${colors.borderColor}`,
                }}
              >
                <DialogHeader>
                  <DialogTitle style={{ color: colors.text }}>Post Details</DialogTitle>
                </DialogHeader>

                <div className="mt-4">
                  {/* Posted Date */}
                  <div className="mb-4 text-sm" style={{ color: colors.textSecondary }}>
                    {format(new Date(selectedPost.published_at), "MMMM d, yyyy")}
                  </div>

                  {/* Image */}
                  {(() => {
                    const imageUrl = getImageUrl(selectedPost.post_content?.media_urls);

                    return imageUrl ? (
                      <div
                        className="mb-6 rounded-lg overflow-hidden"
                        style={{ backgroundColor: colors.bgSecondary }}
                      >
                        <img
                          src={proxyGoogleImage(imageUrl, 1200)}
                          alt="Post image"
                          className="w-full h-auto"
                          onError={(e) => {
                            // Fires only when the (already proxied) URL genuinely fails.
                            console.error("Modal image failed to load after proxy:", imageUrl);
                            const container = e.currentTarget.parentElement;
                            if (container) container.style.display = 'none';
                          }}
                        />
                      </div>
                    ) : null;
                  })()}

                  {/* Text */}
                  <div className="prose max-w-none">
                    <p className="text-lg leading-relaxed" style={{ color: colors.text }}>
                      {selectedPost.post_content?.summary}
                    </p>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LandingPostsSection;